import { Badge, Icon } from '@risksmart-app/atomic-ui';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

export type PolarityBadgeProps = {
  label: string;
  colour: string;
  polarity: 'good' | 'bad';
  severity?: number;
  size?: 'sm' | 'md';
};

/**
 * Compact "band chip" — a colour swatch, the band label, and a polarity
 * indicator that is never colour-only (a visible "On track"/"Off track"
 * text label, reinforced by a decorative icon). Presentation-pure: no data
 * fetching, so it's reusable anywhere a single indicator band needs
 * rendering (the Bands tab, and — per CUS-187 — the register/My Items/
 * report-builder surfaces planned for later PRs).
 */
const PolarityBadge: FC<PolarityBadgeProps> = ({
  label,
  colour,
  polarity,
  size = 'md',
}) => {
  const { t } = useTranslation(['common'], { keyPrefix: 'bands.polarity' });
  const isGood = polarity === 'good';
  const polarityLabel = t(isGood ? 'good' : 'bad');

  return (
    <span
      data-testid={'polarity-badge'}
      className={'inline-flex min-w-0 items-center gap-2'}
    >
      <span
        aria-hidden={true}
        className={'inline-block size-2.5 shrink-0 rounded-full'}
        style={{ backgroundColor: colour }}
      />
      <span className={'truncate'} title={label}>
        {label}
      </span>
      <Badge variant={isGood ? 'success' : 'warning'} size={size} border={true}>
        <Icon
          name={isGood ? 'check-circle' : 'alert-triangle'}
          size={'xs'}
          aria-hidden={true}
        />
        {polarityLabel}
      </Badge>
    </span>
  );
};

export default PolarityBadge;
